/**
 * useScrollToTop — resets window scroll to (0,0) whenever the route's
 * pathname changes. Mount inside a Router context (in each shell wrapper
 * since some routes mount outside a shared shell, e.g. auth + fault routes).
 *
 * Why a hook + manual mount rather than React Router v6's <ScrollRestoration />:
 * ScrollRestoration requires the data-router APIs (createBrowserRouter); the
 * app currently uses the classic <BrowserRouter> + <Routes> tree. Adding a
 * tiny pathname effect is zero-dep and matches the existing pattern.
 *
 * Behavior: scrolls the window, not individual scrollable children. Pages
 * that need to preserve in-page scroll across query-string changes (e.g.,
 * filter changes inside a list page) should opt-out by not using this hook
 * for query-only updates — the effect intentionally fires on full pathname
 * change only, NOT on `search` / `hash` changes.
 *
 * Sub-agent LLLLL (2026-07-29) — anti-punt for Push 102 Track 5 deferral.
 * When UserShell / AdminShell redirect to /sign-in?expired=1&next=<path>,
 * they stash the current window.scrollY into sessionStorage under
 * scrollStorageKey(path). On successful sign-in the user lands back at
 * <path> and this hook restores that scroll offset (with a small
 * rAF-delay so the page has painted at least once). One-shot: the key
 * is removed after read so a later plain navigation doesn't accidentally
 * inherit an old offset.
 *
 * PLAT-378 (Shell 2b.1): Foundation's AppShell scrolls its <main>, not the
 * window. The wrapper (components/AppShell.tsx) registers that element with
 * setShellScroller, and the stash, the restore and the reset all use it while
 * it is mounted; the window otherwise.
 *
 * PLAT-377 (QA fix): a scroll the shell can't take yet waits for it to mount,
 * and a restore retries until the page is tall enough. See setShellScroller.
 *
 * PLAT-377 (QA fix round 2): a restore follows the shell across a remount.
 * After sign-in, the session's re-probe ('loading') can unmount the shell that
 * was mounted when the restore started, and mount a new one when it ends. So
 * each retry scrolls whichever scroller is registered at that frame, never one
 * captured when it started. See scrollPageTo.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sub-agent LLLLL (2026-07-29) — scoped key so the reauth-restore path
 * doesn't collide with any future scroll-position feature. `path` is the
 * pathname + search combo saved at redirect time.
 */
export function scrollStorageKey(path: string): string {
  return `olytic:scroll:reauth:${path}`;
}

let shellScroller: HTMLElement | null = null;
/** The scroller's offset when it last went away, until the next navigation or mount. */
let lastShellY = 0;
/** What the last navigation asked of a scroller that wasn't mounted, until one mounts. */
let pendingY: number | null = null;
/** Bumped by each scroll, so a newer one (or the user's own input) stops a restore's retries. */
let request = 0;
/** The last restore, while its budget lasts: a newly mounted shell resumes it. */
let restore: { id: number; resume: () => void; disarm: () => void } | null = null;
const RESTORE_MS = 3000;
const USER_INPUT = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/**
 * The element the mounted shell scrolls instead of the window; null when it
 * goes away. PLAT-377 QA: React runs these calls on mount and unmount, and also
 * when a Suspense boundary hides the shell while a lazy route loads and shows it
 * again. So a mount takes the scroll the last navigation couldn't apply (the new
 * page is visible by then), and an unmount keeps the offset for the reauth stash:
 * a session re-probe ('loading') unmounts the shell before 'unauthenticated'.
 * A mount also resumes a restore still within its budget (scrollPageTo).
 */
export function setShellScroller(el: HTMLElement | null): void {
  if (el === null) {
    lastShellY = shellScroller?.scrollTop ?? 0;
    shellScroller = null;
    return;
  }
  shellScroller = el;
  lastShellY = 0;
  if (pendingY !== null) {
    const y = pendingY;
    pendingY = null;
    // A restore still within its budget takes this shell itself, keeping its
    // deadline (PLAT-377 review): mounting doesn't start a new budget.
    if (y > 0 && restore !== null) restore.resume();
    else scrollPageTo(y);
  } else {
    restore?.resume();
  }
}

/**
 * The page's scroll offset: the shell's scroller's when one is mounted, else
 * the offset it had when it unmounted (until the next navigation or mount),
 * else the window's.
 */
export function pageScrollY(): number {
  if (shellScroller !== null) return shellScroller.scrollTop;
  return lastShellY > 0 ? lastShellY : window.scrollY;
}

function scrollPageTo(y: number): void {
  const id = ++request;
  restore?.disarm();
  if (y === 0 || typeof requestAnimationFrame !== 'function') {
    if (shellScroller !== null) shellScroller.scrollTo(0, y);
    else window.scrollTo(0, y);
    return;
  }
  // A restore waits a frame for the page to paint, then retries each frame
  // while the page is still too short to reach y (its data is loading), for up
  // to RESTORE_MS, unless a newer scroll or the user's own input takes over.
  // Each frame scrolls the scroller registered then: the shell may unmount and
  // mount a new <main> meanwhile (a session re-probe after sign-in). Once a
  // shell has been seen, a frame with none registered waits for the next one
  // rather than scrolling the window; with no shell at all (the user lane) the
  // window is the page. Reaching y stops the retries but, within the budget,
  // a newly mounted shell resumes them (setShellScroller).
  // The restore stays armed, listening for the user's input, until that input,
  // a newer scroll or the end of the budget disarms it: reaching y on one
  // scroller doesn't end it (PLAT-377 review), so input after that still
  // stops a later remount from snapping back to y.
  const until = Date.now() + RESTORE_MS;
  const listen = typeof window.addEventListener === 'function';
  let sawShell = false;
  let running = false;
  let armed = true;
  const disarm = () => {
    if (!armed) return;
    armed = false;
    running = false;
    clearTimeout(expiry);
    if (listen) for (const t of USER_INPUT) window.removeEventListener(t, cancel, true);
    if (restore?.id === id) restore = null;
    // Ended by its budget or the user's input rather than a newer scroll, it
    // also drops the offset a shell not yet mounted would take (PLAT-377 review).
    if (request === id) pendingY = null;
  };
  const cancel = () => {
    const live = request === id;
    disarm();
    if (live) request += 1;
  };
  const step = () => {
    if (!armed || request !== id || Date.now() > until) return disarm();
    const el = shellScroller;
    if (el !== null) sawShell = true;
    if (el !== null || !sawShell) {
      if (el !== null) el.scrollTo(0, y);
      else window.scrollTo(0, y);
      if ((el !== null ? el.scrollTop : window.scrollY) >= y - 1) {
        running = false;
        return;
      }
    }
    requestAnimationFrame(step);
  };
  const resume = () => {
    if (running || !armed) return;
    running = true;
    requestAnimationFrame(step);
  };
  const expiry = setTimeout(disarm, RESTORE_MS);
  if (listen) {
    for (const t of USER_INPUT)
      window.addEventListener(t, cancel, { capture: true, passive: true });
  }
  restore = { id, resume, disarm };
  resume();
}

export function useScrollToTop(): void {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Sub-agent LLLLL — restore-then-clear when returning from reauth.
    // Best-effort: sessionStorage may throw in private-browsing modes.
    const key = scrollStorageKey(pathname + search);
    let savedY: number | null = null;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) {
          savedY = parsed;
        }
        sessionStorage.removeItem(key);
      }
    } catch {
      /* private browsing / disabled storage — fall through to default */
    }
    const y = savedY ?? 0;
    lastShellY = 0;
    // No scroller mounted: the shell is hidden while the route loads (or there
    // is none, on the user lane). It takes y when it mounts or shows again.
    pendingY = shellScroller === null ? y : null;
    scrollPageTo(y);
  }, [pathname, search]);
}

/**
 * Convenience component — mount once inside a Router subtree to apply the
 * scroll reset to every descendant route. Renders nothing.
 */
export function ScrollToTop(): null {
  useScrollToTop();
  return null;
}
